import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import './Profile.css';

import config from '../config';
const API_URL = config.API_URL;

interface UserBasic {
    id: string;
    firstName: string;
    lastName: string;
    major: string;
    isOnline?: boolean;
}

interface ChatHistory {
    id: string;
    oduserId: string;
    userName: string;
    lastMessage: string;
    timestamp: string;
}

interface BookmarkItem {
    id: string;
    repoId: string;
    repoName: string;
    repoDescription: string;
    createdAt: string;
}

interface ReviewItem {
    id: string;
    repoId: string;
    repoName: string;
    rating: number;
    comment: string;
    createdAt: string;
}

interface UserProfile {
    id: string;
    email: string;
    profile: {
        firstName: string;
        lastName: string;
        studentId: string;
        major: string;
        year: string;
        bio: string;
        interests: string[];
        isInternational: boolean;
        country: string;
    };
    preferences: {
        sameYear: boolean;
        sameMajor: boolean;
        internationalOnly: boolean;
    };
    status: {
        isVerified: boolean;
        isOnline: boolean;
    };
    stats?: {
        totalConnections: number;
        successfulChats: number;
    };
    following: UserBasic[];
    followers: UserBasic[];
    chatHistory: ChatHistory[];
    bookmarks: BookmarkItem[];
    reviews: ReviewItem[];
}

const Profile: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [isOwnProfile, setIsOwnProfile] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'about' | 'following' | 'followers' | 'bookmarks' | 'reviews' | 'chats' | 'settings'>('about');
    const [isFollowing, setIsFollowing] = useState(false);
    
    // Edit form state
    const [editForm, setEditForm] = useState({
        firstName: '',
        lastName: '',
        bio: '',
        major: '',
        year: '',
        interests: '',
        isInternational: false,
        country: ''
    });

    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchProfile();
    }, [id]);

    const fetchProfile = async () => {
        setLoading(true);
        try {
            let response;
            
            if (!id) {
                // Fetch own profile
                if (!token) {
                    navigate('/login');
                    return;
                }
                response = await axios.get(`${API_URL}/users/me/full`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setIsOwnProfile(true);
            } else {
                // Fetch other user's profile
                response = await axios.get(`${API_URL}/users/${id}/full`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                setIsOwnProfile(currentUser?.id === id);
                
                // Check if current user follows this profile
                if (token && currentUser?.id !== id) {
                    checkFollowStatus(id);
                }
            }
            
            if (response.data.success) {
                setProfile(response.data.user);
                if (response.data.user && (isOwnProfile || currentUser?.id === id)) {
                    initEditForm(response.data.user);
                }
            }
        } catch (error: any) {
            console.error('Failed to fetch profile:', error);
            if (error.response?.status === 404) {
                toast.error('User not found');
                navigate('/');
            }
        } finally {
            setLoading(false);
        }
    };

    const checkFollowStatus = async (userId: string) => {
        try {
            const response = await axios.get(`${API_URL}/users/${userId}/follow-status`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsFollowing(response.data.isFollowing);
        } catch (error) {
            console.error('Failed to check follow status:', error);
        }
    };

    const fetchUserContent = async () => {
        if (!token) return;
        
        try {
            // Fetch bookmarks
            const bookmarksRes = await axios.get(`${API_URL}/bookmarks/my`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (bookmarksRes.data.success && profile) {
                setProfile(prev => prev ? { ...prev, bookmarks: bookmarksRes.data.bookmarks } : null);
            }

            // Fetch reviews
            const reviewsRes = await axios.get(`${API_URL}/reviews/my`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (reviewsRes.data.success && profile) {
                setProfile(prev => prev ? { ...prev, reviews: reviewsRes.data.reviews } : null);
            }
        } catch (error) {
            console.error('Failed to fetch user content:', error);
        }
    };

    const initEditForm = (user: UserProfile) => {
        setEditForm({
            firstName: user.profile.firstName || '',
            lastName: user.profile.lastName || '',
            bio: user.profile.bio || '',
            major: user.profile.major || '',
            year: user.profile.year || '',
            interests: user.profile.interests?.join(', ') || '',
            isInternational: user.profile.isInternational || false,
            country: user.profile.country || ''
        });
    };

    const handleSaveProfile = async () => {
        try {
            const response = await axios.put(
                `${API_URL}/users/profile`,
                {
                    firstName: editForm.firstName,
                    lastName: editForm.lastName,
                    bio: editForm.bio,
                    major: editForm.major,
                    year: editForm.year,
                    interests: editForm.interests.split(',').map(i => i.trim()).filter(i => i),
                    isInternational: editForm.isInternational,
                    country: editForm.country
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setProfile(prev => prev ? { ...prev, ...response.data.user } : null);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            setIsEditing(false);
            toast.success('Profile updated!');
        } catch (error) {
            toast.error('Failed to update profile');
        }
    };

    const handleFollow = async () => {
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            if (isFollowing) {
                await axios.delete(`${API_URL}/users/${id}/follow`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setIsFollowing(false);
                toast.success('Unfollowed');
                // Update followers count
                if (profile) {
                    setProfile({
                        ...profile,
                        followers: profile.followers.filter(f => f.id !== currentUser.id)
                    });
                }
            } else {
                await axios.post(`${API_URL}/users/${id}/follow`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setIsFollowing(true);
                toast.success('Following!');
                // Update followers count
                if (profile) {
                    setProfile({
                        ...profile,
                        followers: [...profile.followers, {
                            id: currentUser.id,
                            firstName: currentUser.profile.firstName,
                            lastName: currentUser.profile.lastName,
                            major: currentUser.profile.major
                        }]
                    });
                }
            }
        } catch (error) {
            toast.error('Action failed');
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
    };

    const formatTimeAgo = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return `${diffDays}d ago`;
    };

    if (loading) {
        return (
            <div className="profile-page">
                <div className="loading">Loading profile...</div>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="profile-page">
                <div className="error">Profile not found</div>
            </div>
        );
    }

    return (
        <div className="profile-page">
            {/* Navigation */}
            <nav className="profile-nav">
                <div className="nav-container">
                    <Link to="/" className="nav-logo">
                        <img src="/pngegg.png" alt="NU Connect" className="nav-logo-img" />
                        <span>NU Connect</span>
                    </Link>
                    <div className="nav-links">
                        <Link to="/" className="nav-link">Home</Link>
                        <Link to="/search" className="nav-link">Discover</Link>
                        <Link to="/chat" className="nav-link">Chat</Link>
                        {token && (
                            <Link to="/profile" className="nav-link active">Profile</Link>
                        )}
                    </div>
                </div>
            </nav>

            <div className="profile-container">
                {/* Profile Header */}
                <div className="profile-header">
                    <div className="profile-cover"></div>
                    <div className="profile-header-content">
                        <div className="profile-avatar">
                            {profile.profile.firstName[0]}{profile.profile.lastName[0]}
                        </div>
                        <div className="profile-header-info">
                            <div className="profile-name-row">
                                <h1>{profile.profile.firstName} {profile.profile.lastName}</h1>
                                {profile.status.isOnline && (
                                    <span className="online-badge">● Online</span>
                                )}
                                {profile.status.isVerified && (
                                    <span className="verified-badge">✓ Verified</span>
                                )}
                            </div>
                            <p className="profile-tagline">
                                {profile.profile.major} • {profile.profile.year}
                                {profile.profile.isInternational && ` • 🌍 ${profile.profile.country}`}
                            </p>
                        </div>
                        <div className="profile-header-actions">
                            {isOwnProfile ? (
                                <>
                                    <button 
                                        className="btn-edit"
                                        onClick={() => setIsEditing(!isEditing)}
                                    >
                                        {isEditing ? 'Cancel' : '✏️ Edit Profile'}
                                    </button>
                                    <button className="btn-logout" onClick={handleLogout}>
                                        Logout
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button 
                                        className={`btn-follow ${isFollowing ? 'following' : ''}`}
                                        onClick={handleFollow}
                                    >
                                        {isFollowing ? '✓ Following' : '+ Follow'}
                                    </button>
                                    <Link to="/chat" className="btn-primary">
                                        💬 Message
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Profile Stats */}
                <div className="profile-stats">
                    <button 
                        className={`stat-box clickable ${activeTab === 'followers' ? 'active' : ''}`}
                        onClick={() => setActiveTab('followers')}
                    >
                        <span className="stat-value">{profile.followers?.length || 0}</span>
                        <span className="stat-label">Followers</span>
                    </button>
                    <button 
                        className={`stat-box clickable ${activeTab === 'following' ? 'active' : ''}`}
                        onClick={() => setActiveTab('following')}
                    >
                        <span className="stat-value">{profile.following?.length || 0}</span>
                        <span className="stat-label">Following</span>
                    </button>
                    <button 
                        className={`stat-box clickable ${activeTab === 'chats' ? 'active' : ''}`}
                        onClick={() => setActiveTab('chats')}
                    >
                        <span className="stat-value">{profile.stats?.successfulChats || 0}</span>
                        <span className="stat-label">Chats</span>
                    </button>
                    <div className="stat-box">
                        <span className="stat-value">{profile.profile.interests?.length || 0}</span>
                        <span className="stat-label">Interests</span>
                    </div>
                </div>

                {/* Tabs */}
                <div className="profile-tabs">
                    <button 
                        className={`tab ${activeTab === 'about' ? 'active' : ''}`}
                        onClick={() => setActiveTab('about')}
                    >
                        About
                    </button>
                    <button 
                        className={`tab ${activeTab === 'following' ? 'active' : ''}`}
                        onClick={() => setActiveTab('following')}
                    >
                        Following ({profile.following?.length || 0})
                    </button>
                    <button 
                        className={`tab ${activeTab === 'followers' ? 'active' : ''}`}
                        onClick={() => setActiveTab('followers')}
                    >
                        Followers ({profile.followers?.length || 0})
                    </button>
                    {isOwnProfile && (
                        <>
                            <button 
                                className={`tab ${activeTab === 'bookmarks' ? 'active' : ''}`}
                                onClick={() => setActiveTab('bookmarks')}
                            >
                                Bookmarks ({profile.bookmarks?.length || 0})
                            </button>
                            <button 
                                className={`tab ${activeTab === 'reviews' ? 'active' : ''}`}
                                onClick={() => setActiveTab('reviews')}
                            >
                                Reviews ({profile.reviews?.length || 0})
                            </button>
                            <button 
                                className={`tab ${activeTab === 'chats' ? 'active' : ''}`}
                                onClick={() => setActiveTab('chats')}
                            >
                                Chat History
                            </button>
                            <button 
                                className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
                                onClick={() => setActiveTab('settings')}
                            >
                                Settings
                            </button>
                        </>
                    )}
                </div>

                {/* Tab Content */}
                <div className="profile-content">
                    {/* ABOUT TAB */}
                    {activeTab === 'about' && (
                        <div className="tab-content">
                            {isEditing ? (
                                <div className="edit-form">
                                    <h3>Edit Your Profile</h3>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>First Name</label>
                                            <input
                                                type="text"
                                                value={editForm.firstName}
                                                onChange={(e) => setEditForm({...editForm, firstName: e.target.value})}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Last Name</label>
                                            <input
                                                type="text"
                                                value={editForm.lastName}
                                                onChange={(e) => setEditForm({...editForm, lastName: e.target.value})}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Bio</label>
                                        <textarea
                                            value={editForm.bio}
                                            onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                                            placeholder="Tell others about yourself..."
                                            maxLength={200}
                                        />
                                        <span className="char-count">{editForm.bio.length}/200</span>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Major</label>
                                            <select
                                                value={editForm.major}
                                                onChange={(e) => setEditForm({...editForm, major: e.target.value})}
                                            >
                                                <option value="Computer Science">Computer Science</option>
                                                <option value="Data Science">Data Science</option>
                                                <option value="Information Systems">Information Systems</option>
                                                <option value="Cybersecurity">Cybersecurity</option>
                                                <option value="Engineering">Engineering</option>
                                                <option value="Business">Business</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Year</label>
                                            <select
                                                value={editForm.year}
                                                onChange={(e) => setEditForm({...editForm, year: e.target.value})}
                                            >
                                                <option value="Freshman">Freshman</option>
                                                <option value="Sophomore">Sophomore</option>
                                                <option value="Junior">Junior</option>
                                                <option value="Senior">Senior</option>
                                                <option value="Graduate">Graduate</option>
                                                <option value="PhD">PhD</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Interests (comma separated)</label>
                                        <input
                                            type="text"
                                            value={editForm.interests}
                                            onChange={(e) => setEditForm({...editForm, interests: e.target.value})}
                                            placeholder="coding, basketball, music, gaming..."
                                        />
                                    </div>
                                    <div className="form-group checkbox-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={editForm.isInternational}
                                                onChange={(e) => setEditForm({...editForm, isInternational: e.target.checked})}
                                            />
                                            I'm an international student
                                        </label>
                                    </div>
                                    {editForm.isInternational && (
                                        <div className="form-group">
                                            <label>Country</label>
                                            <input
                                                type="text"
                                                value={editForm.country}
                                                onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                                                placeholder="Your home country"
                                            />
                                        </div>
                                    )}
                                    <button className="btn-save" onClick={handleSaveProfile}>
                                        Save Changes
                                    </button>
                                </div>
                            ) : (
                                <div className="about-section">
                                    <div className="info-card">
                                        <h3>Bio</h3>
                                        <p>{profile.profile.bio || 'No bio yet.'}</p>
                                    </div>
                                    
                                    <div className="info-card">
                                        <h3>Details</h3>
                                        <div className="details-grid">
                                            <div className="detail-item">
                                                <span className="detail-label">📚 Major</span>
                                                <span className="detail-value">{profile.profile.major}</span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="detail-label">🎓 Year</span>
                                                <span className="detail-value">{profile.profile.year}</span>
                                            </div>
                                            {profile.profile.isInternational && (
                                                <div className="detail-item">
                                                    <span className="detail-label">🌍 From</span>
                                                    <span className="detail-value">{profile.profile.country}</span>
                                                </div>
                                            )}
                                            {isOwnProfile && (
                                                <>
                                                    <div className="detail-item">
                                                        <span className="detail-label">📧 Email</span>
                                                        <span className="detail-value">{profile.email}</span>
                                                    </div>
                                                    <div className="detail-item">
                                                        <span className="detail-label">🆔 Student ID</span>
                                                        <span className="detail-value">{profile.profile.studentId}</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className="info-card">
                                        <h3>Interests</h3>
                                        <div className="interests-list">
                                            {profile.profile.interests?.length > 0 ? (
                                                profile.profile.interests.map((interest, idx) => (
                                                    <Link 
                                                        key={idx} 
                                                        to={`/search?interest=${interest}`} 
                                                        className="interest-tag clickable"
                                                    >
                                                        {interest}
                                                    </Link>
                                                ))
                                            ) : (
                                                <p className="no-data">No interests added yet.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* FOLLOWING TAB */}
                    {activeTab === 'following' && (
                        <div className="tab-content">
                            <div className="users-list-section">
                                <h3>Following ({profile.following?.length || 0})</h3>
                                {profile.following && profile.following.length > 0 ? (
                                    <ul className="users-list">
                                        {profile.following.map((user) => (
                                            <li key={user.id} className="user-list-item">
                                                <Link to={`/profile/${user.id}`} className="user-link">
                                                    <div className="user-avatar">
                                                        {user.firstName[0]}{user.lastName[0]}
                                                        {user.isOnline && <span className="online-dot"></span>}
                                                    </div>
                                                    <div className="user-info">
                                                        <span className="user-name">{user.firstName} {user.lastName}</span>
                                                        <span className="user-major">{user.major}</span>
                                                    </div>
                                                </Link>
                                                <Link to={`/profile/${user.id}`} className="view-profile-btn">
                                                    View Profile →
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="empty-state">
                                        <p>{isOwnProfile ? "You're not following anyone yet." : "Not following anyone yet."}</p>
                                        {isOwnProfile && (
                                            <Link to="/search" className="btn-primary">Discover People</Link>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* FOLLOWERS TAB */}
                    {activeTab === 'followers' && (
                        <div className="tab-content">
                            <div className="users-list-section">
                                <h3>Followers ({profile.followers?.length || 0})</h3>
                                {profile.followers && profile.followers.length > 0 ? (
                                    <ul className="users-list">
                                        {profile.followers.map((user) => (
                                            <li key={user.id} className="user-list-item">
                                                <Link to={`/profile/${user.id}`} className="user-link">
                                                    <div className="user-avatar">
                                                        {user.firstName[0]}{user.lastName[0]}
                                                        {user.isOnline && <span className="online-dot"></span>}
                                                    </div>
                                                    <div className="user-info">
                                                        <span className="user-name">{user.firstName} {user.lastName}</span>
                                                        <span className="user-major">{user.major}</span>
                                                    </div>
                                                </Link>
                                                <Link to={`/profile/${user.id}`} className="view-profile-btn">
                                                    View Profile →
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="empty-state">
                                        <p>{isOwnProfile ? "You don't have any followers yet." : "No followers yet."}</p>
                                        {isOwnProfile && (
                                            <Link to="/chat" className="btn-primary">Start Connecting</Link>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* CHAT HISTORY TAB (Own Profile Only) */}
                    {activeTab === 'chats' && isOwnProfile && (
                        <div className="tab-content">
                            <div className="chats-list-section">
                                <h3>Recent Chats</h3>
                                {profile.chatHistory && profile.chatHistory.length > 0 ? (
                                    <ul className="chats-list">
                                        {profile.chatHistory.map((chat) => (
                                            <li key={chat.id} className="chat-list-item">
                                                <Link to={`/profile/${chat.oduserId}`} className="chat-user-link">
                                                    <div className="chat-avatar">
                                                        {chat.userName.split(' ').map(n => n[0]).join('')}
                                                    </div>
                                                    <div className="chat-info">
                                                        <span className="chat-user-name">{chat.userName}</span>
                                                        <span className="chat-preview">{chat.lastMessage}</span>
                                                    </div>
                                                </Link>
                                                <div className="chat-meta">
                                                    <span className="chat-time">{formatTimeAgo(chat.timestamp)}</span>
                                                    <Link to={`/chat?user=${chat.oduserId}`} className="continue-chat-btn">
                                                        Continue Chat →
                                                    </Link>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="empty-state">
                                        <p>No chat history yet.</p>
                                        <Link to="/chat" className="btn-primary">Start Your First Chat</Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* BOOKMARKS TAB (Own Profile Only) */}
                    {activeTab === 'bookmarks' && isOwnProfile && (
                        <div className="tab-content">
                            <div className="bookmarks-section">
                                <h3>Bookmarked Repositories ({profile.bookmarks?.length || 0})</h3>
                                {profile.bookmarks && profile.bookmarks.length > 0 ? (
                                    <ul className="bookmarks-list">
                                        {profile.bookmarks.map((bookmark) => (
                                            <li key={bookmark.id} className="bookmark-item">
                                                <div className="bookmark-info">
                                                    <Link 
                                                        to={`/details/${bookmark.repoId}?name=${encodeURIComponent(bookmark.repoName)}`}
                                                        className="bookmark-name"
                                                    >
                                                        📁 {bookmark.repoName}
                                                    </Link>
                                                    <p className="bookmark-desc">
                                                        {bookmark.repoDescription || 'No description'}
                                                    </p>
                                                    <span className="bookmark-date">
                                                        Saved {formatTimeAgo(bookmark.createdAt)}
                                                    </span>
                                                </div>
                                                <Link 
                                                    to={`/details/${bookmark.repoId}?name=${encodeURIComponent(bookmark.repoName)}`}
                                                    className="view-profile-btn"
                                                >
                                                    View Details →
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="empty-state">
                                        <p>No bookmarks yet.</p>
                                        <Link to="/search" className="btn-primary">Discover Repositories</Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* REVIEWS TAB (Own Profile Only) */}
                    {activeTab === 'reviews' && isOwnProfile && (
                        <div className="tab-content">
                            <div className="reviews-section">
                                <h3>Your Reviews ({profile.reviews?.length || 0})</h3>
                                {profile.reviews && profile.reviews.length > 0 ? (
                                    <ul className="user-reviews-list">
                                        {profile.reviews.map((review) => (
                                            <li key={review.id} className="user-review-item">
                                                <div className="review-header">
                                                    <Link 
                                                        to={`/details/${review.repoId}?name=${encodeURIComponent(review.repoName)}`}
                                                        className="review-repo-name"
                                                    >
                                                        📁 {review.repoName}
                                                    </Link>
                                                    <span className="review-rating">
                                                        {'⭐'.repeat(review.rating)}
                                                    </span>
                                                </div>
                                                <p className="review-comment">{review.comment}</p>
                                                <span className="review-date">
                                                    Reviewed {formatTimeAgo(review.createdAt)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="empty-state">
                                        <p>No reviews yet.</p>
                                        <Link to="/search" className="btn-primary">Find Repositories to Review</Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* SETTINGS TAB (Own Profile Only) */}
                    {activeTab === 'settings' && isOwnProfile && (
                        <div className="tab-content">
                            <div className="settings-section">
                                <h3>Matching Preferences</h3>
                                <div className="settings-list">
                                    <label className="setting-item">
                                        <input type="checkbox" defaultChecked={profile.preferences?.sameYear} />
                                        <span>Prefer matching with students in my year</span>
                                    </label>
                                    <label className="setting-item">
                                        <input type="checkbox" defaultChecked={profile.preferences?.sameMajor} />
                                        <span>Prefer matching with students in my major</span>
                                    </label>
                                    <label className="setting-item">
                                        <input type="checkbox" defaultChecked={profile.preferences?.internationalOnly} />
                                        <span>Only match with international students</span>
                                    </label>
                                </div>

                                <h3>Privacy</h3>
                                <div className="settings-list">
                                    <label className="setting-item">
                                        <input type="checkbox" defaultChecked={true} />
                                        <span>Show my online status</span>
                                    </label>
                                    <label className="setting-item">
                                        <input type="checkbox" defaultChecked={true} />
                                        <span>Allow others to follow me</span>
                                    </label>
                                </div>

                                <h3>Account</h3>
                                <div className="danger-zone">
                                    <button className="btn-danger">Deactivate Account</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Profile;